import { PlusOutlined } from '@ant-design/icons';
import { StateTopWaterbody, Waterbody, WaterbodyDetail } from '@omniafishing/core';
import classNames from 'classnames';
import pluralize from 'pluralize';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useDimensions } from '../../hooks/use_dimensions';
import { useQueryString } from '../../hooks/use_query_string';
import { useResponsive } from '../../hooks/use_responsive';
import { useUserWaterbodies } from '../../hooks/use_user_waterbodies';
import { getBounds } from '../../lib/fit_bounds';
import { OmniaQueryParams } from '../../lib/query_string';
import { OmniaUrls } from '../../lib/urls';
import { hasAccessToken } from '../../redux/auth';
import { getSeasonGroups, getSpecies } from '../../redux/reference_data';
import { RoutePaths } from '../../routes';
import { MapDefault } from '../map_default/map_default';
import { OmniaButton } from '../omnia_button/omnia_button';
import { OmniaLinkButton } from '../omnia_button/omnia_link_button';
import SvgHotbait from '../svg/hotbait';
import SvgMap from '../svg/map';
import SvgShopByLake from '../svg/shop_by_lake';
import { WaterbodyFollowButtonLoggedIn } from '../waterbody_follow_button_logged_in/waterbody_follow_button_logged_in';
import { WaterbodyFollowButtonLoggedOut } from '../waterbody_follow_button_logged_out/waterbody_follow_button_logged_out';
import { StatePageIds } from './state_detail';
import styles from './top_waterbodies.less';

interface TopWaterbodiesProps {
  bbox: string;
  stateName?: string;
  topWaterbodies: StateTopWaterbody[];
  selectedSpeciesName?: string;
  selectedSeasonGroupName?: string;
}

export const TopWaterbodies = (props: TopWaterbodiesProps) => {
  const { topWaterbodies, bbox, stateName, selectedSpeciesName, selectedSeasonGroupName } = props;

  const { stringify } = useQueryString();
  const [measureRef, bounds] = useDimensions({ onResize: true });
  const { isMobile } = useResponsive();
  const isLoggedIn = useSelector(hasAccessToken);
  const { userWaterbodies } = useUserWaterbodies();
  const allSpecies = useSelector(getSpecies);
  const allSeasonGroups = useSelector(getSeasonGroups);
  const selectedSpecies = allSpecies.find((s) => s.name === selectedSpeciesName);
  const selectedSeasonGroup = allSeasonGroups.find((sg) => sg.name === selectedSeasonGroupName);

  const [topWaterbodyShowAll, setTopWaterbodyShowAll] = useState(false);
  const topWaterbodiesWaterbodies = topWaterbodies.map((tw) => tw.waterbody);
  const topWaterbodiesSlice = topWaterbodies.slice(0, 20);
  const topWaterbodyToDisplay = topWaterbodyShowAll
    ? topWaterbodiesSlice
    : isMobile
    ? topWaterbodies.slice(0, 5)
    : topWaterbodiesSlice;

  const mapSize = {
    ...bounds,
    height: isMobile ? 300 : 500,
  };

  const bboxNums = bbox.split(',').map((n) => Number(n));
  const waterbodyUrl = (waterbody: Waterbody) => {
    const url = OmniaUrls.waterbody(waterbody);
    const queryParams: Record<string, string> = {};
    if (selectedSpecies) {
      queryParams[OmniaQueryParams.species] = selectedSpecies.name;
    }
    if (selectedSeasonGroup) {
      queryParams[OmniaQueryParams.season_group] = selectedSeasonGroup.name;
    }
    return url + `?${stringify(queryParams)}`;
  };

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.mapCol}>
          <div ref={measureRef}>{/* empty for measuring width */}</div>
          <div className={styles.topLakesMap}>
            {bounds.width > 0 && (
              <>
                <MapDefault
                  longitude={getBounds(mapSize, bboxNums).longitude}
                  latitude={getBounds(mapSize, bboxNums).latitude}
                  zoom={getBounds(mapSize, bboxNums).zoom}
                  featuredMarkers={topWaterbodiesWaterbodies}
                  disableScrollZoom
                  disablePinFetch
                  showLegend
                  showSatelliteToggle
                  placeHolderText="Find a Lake"
                  children={
                    <OmniaLinkButton
                      size="lg"
                      kind="tertiary"
                      to={RoutePaths.MAP}
                      className={styles.mapLink}
                      fontSize={14}
                    >
                      <SvgMap width={20} height={20} className={styles.mapLinkIcon} /> Find Your
                      Lake
                    </OmniaLinkButton>
                  }
                />
              </>
            )}
          </div>
        </div>
        <div className={styles.lakesCol}>
          {/* TODO: maybe make this heading a prop */}
          {stateName && (
            <h2 className={styles.h3} id={StatePageIds.top_lakes}>
              Top {stateName} Lakes
            </h2>
          )}
          {topWaterbodyToDisplay.length > 0 ? (
            <>
              <ol className={styles.topWaterbodies}>
                {topWaterbodyToDisplay.map((topWaterbody, i) => {
                  const { waterbody, recent_fishing_reports_count, fishing_reports_count } =
                    topWaterbody;
                  const { primary_name } = waterbody;
                  const userWaterBody = userWaterbodies.find(
                    (w) => w.waterbody_id === waterbody.waterbody_id
                  );
                  return (
                    <li className={styles.topWaterbody} key={waterbody.waterbody_id}>
                      <Link to={waterbodyUrl(waterbody)}>
                        <div>
                          <span className={styles.topWaterbodyName}>
                            {i + 1}. {primary_name}
                          </span>
                          <span className={styles.topWaterbodyStat}>
                            <SvgShopByLake
                              width={16}
                              height={16}
                              className={styles.topWaterbodyIcon}
                            />
                            {fishing_reports_count}{' '}
                            {pluralize('Fishing Report', fishing_reports_count)}
                          </span>
                          {recent_fishing_reports_count > 0 && (
                            <span
                              className={classNames(
                                styles.topWaterbodyStat,
                                styles.topWaterbodyStat__recent
                              )}
                            >
                              <SvgHotbait
                                width={16}
                                height={16}
                                className={styles.topWaterbodyIcon}
                              />
                              {recent_fishing_reports_count}{' '}
                              {pluralize('New Report', recent_fishing_reports_count)}
                            </span>
                          )}
                        </div>
                      </Link>
                      <div className={styles.buttonContainer}>
                        {isLoggedIn ? (
                          <WaterbodyFollowButtonLoggedIn
                            alwaysMicroButton
                            waterbody={
                              !!userWaterBody ? userWaterBody : (waterbody as WaterbodyDetail)
                            }
                            className={styles.followButton}
                            buttonType="tertiary"
                            iconSize={16}
                            noPopup
                          />
                        ) : (
                          <WaterbodyFollowButtonLoggedOut
                            alwaysMicroButton
                            waterbody={waterbody as WaterbodyDetail}
                            className={styles.followButton__loggedOut}
                            context={'state_page'}
                            buttonType="tertiary"
                            defaultSpeciesName={selectedSpeciesName}
                          />
                        )}
                        <OmniaLinkButton
                          size="lg"
                          kind="secondary"
                          fontSize={14}
                          to={waterbodyUrl(waterbody)}
                          className={styles.lakeLinkButton}
                        >
                          View
                        </OmniaLinkButton>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </>
          ) : (
            <>
              <p className={styles.noLakes}>
                There aren't any lakes{' '}
                {selectedSeasonGroup != null && selectedSpecies != null && (
                  <>
                    for <span>{selectedSeasonGroup.display_name}</span>{' '}
                    <span>{selectedSpecies.display_name}</span>
                  </>
                )}
              </p>
            </>
          )}

          {topWaterbodyToDisplay.length < topWaterbodiesWaterbodies.length &&
            isMobile &&
            !topWaterbodyShowAll && (
              <OmniaButton
                kind="text"
                size="lg"
                onClick={() => {
                  setTopWaterbodyShowAll(true);
                }}
                className={styles.showMoreButton}
              >
                <PlusOutlined />
                Show More
              </OmniaButton>
            )}
        </div>
      </div>
    </>
  );
};
